import React from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import Logo from '../Logo';

const LogoLoader = ({ size = 88, showLabel = true }) => {
	const theme = useTheme();
	const isDark = theme.palette.mode === 'dark';

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				gap: 3,
			}}
		>
			<Box
				sx={{
					position: 'relative',
					width: size + 64,
					height: size + 64,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				{/* Outer liquid ring */}
				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						borderRadius: '50%',
						background: isDark
							? `conic-gradient(from 0deg, ${alpha('#5A8AAD', 0.45)}, ${alpha('#7BA3C4', 0.25)}, ${alpha('#6B9BA8', 0.35)}, ${alpha('#5A8AAD', 0.45)})`
							: `conic-gradient(from 0deg, ${alpha('#4A7A9C', 0.35)}, ${alpha('#7BA3C4', 0.2)}, ${alpha('#6B9BA8', 0.28)}, ${alpha('#4A7A9C', 0.35)})`,
						animation: 'liquidSpin 3s linear infinite',
						'@keyframes liquidSpin': {
							to: { transform: 'rotate(360deg)' },
						},
						'&::after': {
							content: '""',
							position: 'absolute',
							inset: 3,
							borderRadius: '50%',
							background: isDark ? '#0A0A0F' : '#F2F2F7',
						},
					}}
				/>

				{/* Glass orb */}
				<Box
					sx={{
						position: 'absolute',
						inset: 12,
						borderRadius: '50%',
						background: isDark
							? alpha('#FFFFFF', 0.06)
							: alpha('#FFFFFF', 0.85),
						backdropFilter: 'blur(20px) saturate(180%)',
						WebkitBackdropFilter: 'blur(20px) saturate(180%)',
						border: '1px solid',
						borderColor: isDark
							? alpha('#FFFFFF', 0.12)
							: alpha('#FFFFFF', 0.8),
						boxShadow: isDark
							? `0 8px 32px ${alpha('#000000', 0.4)}, inset 0 1px 0 ${alpha('#FFFFFF', 0.1)}`
							: `0 8px 32px ${alpha('#000000', 0.08)}, inset 0 1px 0 ${alpha('#FFFFFF', 1)}`,
						animation: 'liquidPulse 2.4s ease-in-out infinite',
						'@keyframes liquidPulse': {
							'0%, 100%': { transform: 'scale(1)' },
							'50%': { transform: 'scale(1.03)' },
						},
					}}
				/>

				{/* Inner glow */}
				<Box
					sx={{
						position: 'absolute',
						inset: 20,
						borderRadius: '50%',
						background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.2)} 0%, transparent 70%)`,
						animation: 'liquidGlow 2s ease-in-out infinite',
						'@keyframes liquidGlow': {
							'0%, 100%': { opacity: 0.4 },
							'50%': { opacity: 1 },
						},
					}}
				/>

				<Logo
					sx={{
						position: 'relative',
						zIndex: 1,
						width: size * 0.65,
						height: 'auto',
						filter: `drop-shadow(0 2px 8px ${alpha(theme.palette.primary.main, 0.3)})`,
					}}
				/>
			</Box>

			{showLabel && (
				<Box sx={{ textAlign: 'center' }}>
					<Typography
						variant="caption"
						sx={{
							display: 'block',
							fontWeight: 500,
							letterSpacing: 2,
							textTransform: 'uppercase',
							color: 'text.secondary',
							fontSize: '0.65rem',
							mb: 1.5,
							opacity: 0.8,
						}}
					>
						Loading
					</Typography>
					<Box
						sx={{
							width: 140,
							height: 4,
							borderRadius: 4,
							bgcolor: alpha(theme.palette.primary.main, 0.1),
							overflow: 'hidden',
							position: 'relative',
						}}
					>
						<Box
							sx={{
								position: 'absolute',
								height: '100%',
								width: '50%',
								borderRadius: 4,
								background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
								animation: 'liquidShimmer 1.4s ease-in-out infinite',
								'@keyframes liquidShimmer': {
									'0%': { transform: 'translateX(-100%)' },
									'100%': { transform: 'translateX(300%)' },
								},
							}}
						/>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default LogoLoader;
